import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
// Import Role directly from generated types to avoid Rollup tree-shaking issues
/* ---------------------------------------------------------------------------
   Local Role enum
   ---------------------------------------------------------------------------
   We define the Role enum here to avoid bundler resolution issues with the
   shared package during the production build. Keep the string literals in
   sync with the backend & shared enums.
--------------------------------------------------------------------------- */
enum Role {
  ADMIN = 'ADMIN',
  DEVELOPER = 'DEVELOPER',
}

// Icons
import {
  UsersIcon,
  CodeBracketIcon,
  Squares2X2Icon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowLeftIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const AdminLayout: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Check if user is admin
  if (user?.role !== Role.ADMIN) {
    navigate('/');
    return null;
  }

  // Admin navigation items
  const adminNavItems = [
    { to: '/admin', icon: <Squares2X2Icon className="w-5 h-5" />, label: 'Dashboard', exact: true },
    {
      to: '/admin/snippets',
      icon: <CodeBracketIcon className="w-5 h-5" />,
      label: 'Snippets',
      exact: false,
    },
    { to: '/admin/users', icon: <UsersIcon className="w-5 h-5" />, label: 'Users', exact: false },
    {
      to: '/admin/stats',
      icon: <ChartBarIcon className="w-5 h-5" />,
      label: 'Statistics',
      exact: false,
    },
    {
      to: '/admin/settings',
      icon: <Cog6ToothIcon className="w-5 h-5" />,
      label: 'Settings',
      exact: false,
    },
  ];

  // Check if a nav item is active
  const isActive = (path: string, exact: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Toggle sidebar on desktop
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Toggle sidebar on mobile
  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Admin Header */}
      <header className="navbar bg-dark-900">
        <div className="container mx-auto flex justify-between items-center">
          {/* Logo and back button */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="btn-ghost rounded-full p-2 text-gray-300 hover:text-white"
              aria-label="Back to dashboard"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2">
              {/* New logo image */}
              <img src="/logo.png" alt="Snippix" className="h-7 w-auto" />
              <div className="flex flex-col">
                <span className="text-xs text-primary">Admin Panel</span>
              </div>
            </div>
          </div>

          {/* Mobile sidebar toggle */}
          <button
            className="md:hidden btn-ghost rounded-full p-2"
            onClick={toggleMobileSidebar}
            aria-label="Toggle admin menu"
          >
            {isMobileSidebarOpen ? (
              <XMarkIcon className="w-6 h-6" />
            ) : (
              <Bars3Icon className="w-6 h-6" />
            )}
          </button>

          {/* Desktop sidebar toggle */}
          <button
            className="hidden md:block btn-ghost rounded-full p-2"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
          >
            {isSidebarOpen ? <XMarkIcon className="w-5 h-5" /> : <Bars3Icon className="w-5 h-5" />}
          </button>
        </div>
      </header>

      <div className="flex flex-1 pt-16">
        {/* Desktop Sidebar */}
        <aside
          className={`fixed top-0 left-0 h-full bg-dark-800 border-r border-dark-600 transition-all duration-300 ease-in-out z-10 pt-16 ${
            isSidebarOpen ? 'w-64' : 'w-0 -translate-x-full md:w-20 md:translate-x-0'
          } hidden md:block`}
        >
          <nav className="p-4 h-full overflow-y-auto">
            {isSidebarOpen ? (
              <ul className="space-y-2">
                {adminNavItems.map(item => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.exact}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary bg-opacity-10 text-primary'
                            : 'text-gray-300 hover:text-white hover:bg-dark-700'
                        }`
                      }
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="space-y-2">
                {adminNavItems.map(item => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.exact}
                      className={`flex justify-center p-3 rounded-lg transition-colors ${
                        isActive(item.to, item.exact)
                          ? 'bg-primary bg-opacity-10 text-primary'
                          : 'text-gray-300 hover:text-white hover:bg-dark-700'
                      }`}
                      title={item.label}
                    >
                      {item.icon}
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </nav>
        </aside>

        {/* Mobile Sidebar */}
        <AnimatePresence>
          {isMobileSidebarOpen && (
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="fixed top-0 left-0 h-full w-64 bg-dark-800 border-r border-dark-600 z-50 pt-16"
            >
              <nav className="p-4 h-full overflow-y-auto">
                <ul className="space-y-2">
                  {adminNavItems.map(item => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.exact}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                            isActive
                              ? 'bg-primary bg-opacity-10 text-primary'
                              : 'text-gray-300 hover:text-white hover:bg-dark-700'
                          }`
                        }
                        onClick={() => setIsMobileSidebarOpen(false)}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </nav>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Mobile sidebar backdrop */}
        {isMobileSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main
          className={`flex-1 transition-all duration-300 ease-in-out ${
            isSidebarOpen ? 'md:ml-64' : 'md:ml-20'
          }`}
        >
          <div className="container mx-auto px-4 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
